import * as React from 'react';
import { Upload, message } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';

export type ImageUploadHandle = {
    openFilePicker: () => void;
};

type Props = {
    value?: File | null;
    previewUrl?: string;
    placeholder?: React.ReactNode;
    disabled?: boolean;
    onChange: (file: File | null) => void;
};

const allowedFileTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxFileSizeBytes = 2 * 1024 * 1024;

const ImageUpload = React.forwardRef<ImageUploadHandle, Props>(
    ({ value, previewUrl, placeholder, disabled, onChange }, ref) => {
        const fileInputRef = React.useRef<HTMLInputElement>(null);
        const [localPreviewUrl, setLocalPreviewUrl] = React.useState<string | undefined>(previewUrl);
        const [isLoading, setIsLoading] = React.useState(false);

        React.useImperativeHandle(ref, () => ({
            openFilePicker: () => {
                fileInputRef.current?.click();
            }
        }));

        React.useEffect(() => {
            let objectUrl: string | undefined;

            if (value) {
                objectUrl = URL.createObjectURL(value);
                setLocalPreviewUrl(objectUrl);
            } else {
                setLocalPreviewUrl(previewUrl);
            }

            return () => {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
            };
        }, [previewUrl, value]);

        const selectFile = (file: File) => {
            if (!allowedFileTypes.includes(file.type)) {
                message.error('Only JPEG, PNG, and WebP images are allowed.');
                return false;
            }

            if (file.size > maxFileSizeBytes) {
                message.error('Image size cannot exceed 2 MB.');
                return false;
            }

            setIsLoading(true);
            onChange(file);
            setIsLoading(false);

            return false;
        };

        const beforeUpload = (file: RcFile) => selectFile(file);

        const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = '';

            if (!file || disabled) {
                return;
            }

            selectFile(file);
        };

        const uploadButton = (
            <div>
                {isLoading ? <LoadingOutlined /> : <PlusOutlined />}
                <div className="ant-upload-text">Upload</div>
            </div>
        );

        return (
            <div>
                <input
                    ref={fileInputRef}
                    data-testid="shortcut-image-file-input"
                    type="file"
                    accept=".jpeg,.jpg,.png,.webp"
                    disabled={disabled}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                />
                <Upload
                    accept=".jpeg,.jpg,.png,.webp"
                    name="shortcut-image"
                    listType="picture-card"
                    className="icon-uploader"
                    showUploadList={false}
                    disabled={disabled}
                    beforeUpload={beforeUpload}
                >
                    {localPreviewUrl ? <img src={localPreviewUrl} alt="shortcut" /> : placeholder || uploadButton}
                </Upload>
            </div>
        );
    }
);

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
